// src/main.jsx
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './styles/index.css';
import './utils/standaloneGuards.js';

import App from './App.jsx';
import { Auth0Provider } from '@auth0/auth0-react';
import { PATHS, sanitizeReturnToPath } from './utils/routes.js';
import { getAuthCallbackUrl } from './utils/auth0Config.js';
import { applyLocaleToDocument, getInitialLocale, toAuth0UiLocales } from './utils/locale.js';
import { initPwa } from './pwa.js';

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("❌ Élément 'root' introuvable dans le DOM");
}

const initialLocale = getInitialLocale();
applyLocaleToDocument(initialLocale);

const AUTH_CALLBACK_META_KEY = 'rt.auth.callback.meta';

const setAuthCallbackMeta = (returnTo) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      AUTH_CALLBACK_META_KEY,
      JSON.stringify({ ts: Date.now(), returnTo })
    );
  } catch {
    // ignore sessionStorage errors
  }
};

const preloadImage = (href, type = 'image/webp') => {
  if (typeof document === 'undefined' || !href) return;
  const selector = `link[rel="preload"][as="image"][href="${href}"]`;
  if (document.head.querySelector(selector)) return;
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = href;
  if (type) link.type = type;
  link.setAttribute('fetchpriority', 'high');
  document.head.appendChild(link);
};

const preconnect = (href) => {
  if (typeof document === 'undefined' || !href) return;
  const selector = `link[rel="preconnect"][href="${href}"]`;
  if (document.head.querySelector(selector)) return;
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = href;
  link.crossOrigin = '';
  document.head.appendChild(link);
};

const preloadCriticalRouteImage = () => {
  if (typeof window === 'undefined') return;
  const path = window.location?.pathname || '/';
  if (path === '/') {
    preloadImage('/icon-txt.svg', 'image/svg+xml');
    return;
  }
  if (path !== '/client/accueil') return;
  preconnect('https://res.cloudinary.com');
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 11) {
    preloadImage('/images/cover-morning.webp');
  } else if (hour >= 11 && hour < 17) {
    preloadImage('/images/cover-sunny.webp');
  } else if (hour >= 17 && hour < 21) {
    preloadImage('/images/cover-evening.webp');
  } else {
    preloadImage('/images/cover-winter.webp');
  }
};

preloadCriticalRouteImage();
initPwa();

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn && import.meta.env.PROD) {
  const pathname = typeof window !== 'undefined' ? window.location.pathname || '' : '';
  const isPerfCriticalPublicPath = (
    pathname === '/' ||
    pathname === '/client/accueil' ||
    pathname.startsWith('/vitrine/')
  );
  const shouldSkipSentryBootstrap = (
    isPerfCriticalPublicPath &&
    import.meta.env.VITE_SENTRY_PUBLIC_BOOTSTRAP !== '1'
  );

  if (!shouldSkipSentryBootstrap) {
  const bootSentry = () => {
    import('@sentry/react').then((Sentry) => {
      Sentry.init({
        dsn: sentryDsn,
        environment: import.meta.env.MODE,
        tracesSampleRate: 0.1,
      });
    }).catch(() => {
      // ignore sentry bootstrap errors to avoid blocking app startup
    });
  };

  let bootScheduled = false;
  const runBoot = () => {
    if (bootScheduled) return;
    bootScheduled = true;
    bootSentry();
  };

  const scheduleBoot = () => {
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const timeout = 4000;
      window.requestIdleCallback(runBoot, { timeout });
      return;
    }
    const delay = 2400;
    setTimeout(runBoot, delay);
  };

  if (document.readyState === 'complete') {
    scheduleBoot();
  } else {
    window.addEventListener('load', () => {
      scheduleBoot();
    }, { once: true });
  }
  }
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}          // ex: auth.riveltime.app
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}     // 👈 prends depuis .env
      onRedirectCallback={(appState) => {
        const safeReturnTo = sanitizeReturnToPath(appState?.returnTo, PATHS.root);
        setAuthCallbackMeta(safeReturnTo);
        const nextPath = safeReturnTo && safeReturnTo !== PATHS.root
          ? `${PATHS.authCallback}?returnTo=${encodeURIComponent(safeReturnTo)}`
          : PATHS.authCallback;
        window.history.replaceState({}, document.title, nextPath);
      }}
      authorizationParams={{
        redirect_uri: getAuthCallbackUrl(),
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,    // ex: https://api.riveltime.app
        scope: 'openid profile email offline_access',
        ui_locales: toAuth0UiLocales(initialLocale),
      }}
      cacheLocation="localstorage"
      useRefreshTokens
      useRefreshTokensFallback={false}
      useCookiesForTransactions
      sessionCheckExpiryDays={30}
    >
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </Auth0Provider>
  </React.StrictMode>
);
